'use client'

import React, { useState } from 'react'
import { Button, Card, CardBody, CardHeader, Link } from '@heroui/react'
import * as SolarIcons from 'solar-icon-set'
import { motion } from 'framer-motion'

import { useSpotlightEffect } from '@/hooks/cursors'
import { TOOLS } from '@/constants/tools'

export default function ErrorPage() {
  const canvasRef = useSpotlightEffect({
    spotlightSize: 200,
    spotlightIntensity: 1,
    fadeSpeed: 0.2,
    glowColor: '243, 18, 96',
    pulseSpeed: 1500
  })

  const [tool, setTool] = useState(TOOLS[0])

  const { key, description, label, Icon, href } = tool

  const [direction, setDirection] = useState('right')

  const handleNextTool = () => {
    const currentIndex = TOOLS.findIndex((t) => t.key === tool.key)
    const nextIndex = (currentIndex + 1) % TOOLS.length

    setTool(TOOLS[nextIndex])
    setDirection('right')
  }

  const handlePrevTool = () => {
    const currentIndex = TOOLS.findIndex((t) => t.key === tool.key)
    const prevIndex = (currentIndex - 1 + TOOLS.length) % TOOLS.length

    setTool(TOOLS[prevIndex])
    setDirection('left')
  }

  return (
    <main className="relative w-dvw h-dvh overflow-hidden grid place-items-center">
      <div className="flex flex-col lg:flex-row items-center justify-center">
        <span className="text-[39vw] font-geist-black text-center select-none">
          5
        </span>
        <div className="bg-current max-w-screen-sm lg:w-[45dvw] lg:h-[40dvh] rounded-xl grid place-items-center">
          <div className="flex w-full flex-col items-center gap-4 pt-10 pb-6 px-6">
            <h1 className="text-background font-geist-black relative z-10 text-center">
              <span className="text-danger h1 text-shadow animate-pulse">
                ...500 ERROR...
              </span>
              <br />
              <span className="h3">Something went wrong</span>
            </h1>
            <p className="text-background text-center text-[clamp(12px,_0.75vw,_16px)]">
              Go to the dashboard and check out our tools to enhance your
              streaming experience.
            </p>
            <div className="flex justify-between w-full items-center gap-4">
              <Button
                isIconOnly
                className="outline-[3px] bg-black relative z-10 outline-dashed outline-background outline-offset-2"
                radius="sm"
                size="sm"
                onPress={handlePrevTool}
              >
                <SolarIcons.ArrowLeft iconStyle="Bold" />
              </Button>
              <Card
                key={key}
                layout
                animate={{
                  opacity: 1,
                  scale: 1,
                  left: 0
                }}
                as={motion.div}
                classNames={{
                  base: 'outline-[3px] w-3/4 sm:w-96 lg:max-w-max outline-dashed outline-background outline-offset-2',
                  body: 'pt-0 text-xs relative z-10 text-balance text-center',
                  header: 'justify-center'
                }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                  left: direction === 'right' ? '5%' : '-5%'
                }}
                initial={{
                  opacity: 0,
                  scale: 0.95,
                  left: direction === 'right' ? '-5%' : '5%'
                }}
                radius="sm"
              >
                <CardHeader>
                  <Link
                    className="flex items-center gap-2"
                    color="foreground"
                    href={href}
                    size="sm"
                    underline="hover"
                  >
                    {Icon && <Icon iconStyle="Bold" />}
                    <p>{label}</p>
                  </Link>
                </CardHeader>
                <CardBody>
                  <p>{description}</p>
                </CardBody>
              </Card>
              <Button
                isIconOnly
                className="outline-[3px] relative z-10 bg-black outline-dashed outline-background outline-offset-2"
                radius="sm"
                size="sm"
                onPress={handleNextTool}
              >
                <SolarIcons.ArrowRight iconStyle="Bold" />
              </Button>
            </div>
          </div>
        </div>
        <span className="text-[39vw] font-geist-black text-center select-none">
          0
        </span>
      </div>
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 size-full pointer-events-none"
      />
    </main>
  )
}
